import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCameraOSD } from "@/contexts/CameraOSDContext";
import heroFramePhoto from "@/assets/hero-frame-photo.jpg";

const hslToRgba = (hsl: string, alpha: number) => {
  const [h = 0, s = 0, l = 0] = hsl.match(/[\d.]+/g)?.map(Number) ?? [];
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;
  const [r, g, b] = h < 60 ? [chroma, x, 0] : h < 120 ? [x, chroma, 0] : h < 180 ? [0, chroma, x] : h < 240 ? [0, x, chroma] : h < 300 ? [x, 0, chroma] : [chroma, 0, x];

  return `rgba(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}, ${Math.max(0, Math.min(1, alpha))})`;
};

const HeroSection = () => {
  const { t } = useLanguage();
  const { brand, toggle } = useCameraOSD();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tc, setTc] = useState("00:00:00:00");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);

  // Track mobile breakpoint (matches Tailwind md: 768px)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Parallax: hero fades + lekko skaluje w dół przy scrollu (efekt webflow.com)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const vh = window.innerHeight || 1;
      // 0 -> 1 across first viewport
      const p = Math.min(1, Math.max(0, y / vh));
      setScrollProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Timecode HH:MM:SS:FF @ 24fps — current wall-clock time
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      const f = Math.floor((now.getMilliseconds() / 1000) * 24);
      const pad = (n: number) => n.toString().padStart(2, "0");
      setTc(`${pad(h)}:${pad(m)}:${pad(s)}:${pad(f)}`);
    };
    tick();
    const id = window.setInterval(tick, 1000 / 12);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    const mouse = { x: -9999, y: -9999 };
    const MOUSE_RADIUS = 150;

    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];

    // Cached layout values — read only on resize/scroll, not every frame.
    let cssW = 0;
    let cssH = 0;
    let rectLeft = 0;
    let rectTop = 0;

    // Cached theme/style values — recomputed only when the theme actually changes.
    let accent = "";
    let webHsl = "";
    let opacityMul = 1;
    let lineMul = 1;
    let lineWidth = 0.5;

    const refreshTheme = () => {
      const style = getComputedStyle(document.documentElement);
      accent = style.getPropertyValue("--accent").trim();
      const foreground = style.getPropertyValue("--foreground").trim();
      const background = style.getPropertyValue("--background").trim();
      const backgroundLightness = Number(background.split(/\s+/)[2]?.replace("%", "") ?? 100);
      const isDark = document.documentElement.classList.contains("dark") || backgroundLightness < 30;
      webHsl = isDark ? "0 0% 100%" : foreground;
      opacityMul = isDark ? 4.5 : 1;
      lineMul = isDark ? 3.8 : 1;
      lineWidth = isDark ? 1.15 : 0.5;
    };

    const resize = () => {
      cssW = canvas.offsetWidth;
      cssH = canvas.offsetHeight;
      canvas.width = cssW * 2;
      canvas.height = cssH * 2;
      ctx.scale(2, 2);
      const r = canvas.getBoundingClientRect();
      rectLeft = r.left;
      rectTop = r.top;
    };

    const initParticles = () => {
      const count = Math.floor(cssW * cssH / 12000);
      particles = Array.from({ length: Math.min(count, 80) }, () => ({
        x: Math.random() * cssW,
        y: Math.random() * cssH,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.3 + 0.05
      }));
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX - rectLeft;
      mouse.y = e.clientY - rectTop;
    };

    const handleMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const draw = () => {
      ctx.clearRect(0, 0, cssW, cssH);

      // Mouse-interaction line stays accent (red) for that signature pop.

      particles.forEach((p, i) => {
        // Mouse repulsion
        const mdx = p.x - mouse.x;
        const mdy = p.y - mouse.y;
        const mDist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mDist < MOUSE_RADIUS && mDist > 0) {
          const force = (MOUSE_RADIUS - mDist) / MOUSE_RADIUS * 0.8;
          p.vx += mdx / mDist * force;
          p.vy += mdy / mDist * force;
        }

        // Damping
        p.vx *= 0.98;
        p.vy *= 0.98;

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > cssW) p.vx *= -1;
        if (p.y < 0 || p.y > cssH) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = hslToRgba(webHsl, p.opacity * 1.6 * opacityMul);
        ctx.fill();

        // Draw connections
        for (let j = i + 1; j < particles.length; j++) {
          const dx = p.x - particles[j].x;
          const dy = p.y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = hslToRgba(webHsl, 0.22 * lineMul * (1 - dist / 120));
            ctx.lineWidth = lineWidth;
            ctx.stroke();
          }
        }

        // Draw line to mouse if close
        if (mDist < MOUSE_RADIUS) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = hslToRgba(accent, 0.5 * (1 - mDist / MOUSE_RADIUS));
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      });

      animationId = requestAnimationFrame(draw);
    };

    resize();
    refreshTheme();
    initParticles();
    draw();

    const parent = canvas.parentElement!;
    parent.addEventListener("mousemove", handleMouseMove);
    parent.addEventListener("mouseleave", handleMouseLeave);

    const onResize = () => {resize();initParticles();};
    window.addEventListener("resize", onResize);

    // Update cached rect on scroll (passive, no layout work in handler).
    const onScroll = () => {
      const r = canvas.getBoundingClientRect();
      rectLeft = r.left;
      rectTop = r.top;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Re-read theme tokens only when the <html> class list changes (theme switch).
    const themeObserver = new MutationObserver(refreshTheme);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      cancelAnimationFrame(animationId);
      parent.removeEventListener("mousemove", handleMouseMove);
      parent.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      themeObserver.disconnect();
    };
  }, []);

  const scrollToAbout = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative h-screen w-full flex flex-col items-center justify-center px-6 overflow-hidden bg-background scanlines"
      style={{
        opacity: 1 - scrollProgress * 0.85,
        transform: `scale(${1 - scrollProgress * 0.06}) translateY(${scrollProgress * -40}px)`,
        transformOrigin: "center center",
        willChange: "transform, opacity",
      }}>
      {/* Animated particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none opacity-100" />

      {/* ============ CAMERA OSD OVERLAY (ARRI / RED) ============ */}
      {/* RED signature: red bar across top of frame when REC */}
      {brand === "RED" && (
        <div className="pointer-events-none absolute top-5 md:top-6 left-5 right-5 md:left-6 md:right-6 h-[3px] bg-accent z-20 animate-pulse" />
      )}
      {/* Frame line */}
      <div className="pointer-events-none absolute inset-5 md:inset-6 border border-foreground/30 z-20" />
      {/* 2.39:1 cinemascope safe area (dashed) — ARRI only */}
      {brand === "ARRI" && (
        <div
          className="pointer-events-none absolute left-5 right-5 md:left-6 md:right-6 border-y border-dashed border-foreground/25 z-20"
          style={{
            top: "50%",
            transform: "translateY(-50%)",
            height: "min(calc((100vw - 48px) / 2.39), 70%)",
          }}
        />
      )}
      {/* RED uses action-safe nested rectangles */}
      {brand === "RED" && (
        <div className="pointer-events-none absolute inset-10 md:inset-14 border border-foreground/15 z-20" />
      )}

      {/* Top OSD bar */}
      <div className="pointer-events-none absolute top-8 md:top-10 left-10 right-10 md:left-12 md:right-12 z-20 flex items-center justify-between font-mono text-[10px] tracking-widest text-foreground/85">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="text-accent">REC</span>
          </span>
          <span className="tabular-nums">{tc}</span>
        </div>
        {brand === "ARRI" ? (
          <div className="hidden md:flex items-center gap-3">
            <span><span className="text-[hsl(var(--osd-yellow))]">EI</span> 800</span>
            <span className="opacity-50">·</span>
            <span>4.5K LF 16:9</span>
            <span className="opacity-50">·</span>
            <span>24.000</span>
            <span className="opacity-50">·</span>
            <span>LogC3</span>
            <span className="opacity-50">·</span>
            <span>ProRes 4444 XQ</span>
          </div>
        ) : (
          <div className="hidden md:flex items-center gap-3 text-foreground/90">
            <span className="text-accent font-bold">R</span>
            <span className="opacity-50">·</span>
            <span>ISO 800</span>
            <span className="opacity-50">·</span>
            <span>6K WS 17:9</span>
            <span className="opacity-50">·</span>
            <span>24.000</span>
            <span className="opacity-50">·</span>
            <span>RC 8:1</span>
            <span className="opacity-50">·</span>
            <span>Log3G10/RWG</span>
          </div>
        )}
        <div className="tabular-nums">
          {brand === "ARRI" ? "A007_C012_0427MW" : "A001_C012_0427MW.R3D"}
        </div>
      </div>

      {/* Easter-egg toggle: small clickable badge bottom-right of frame */}
      <button
        onClick={toggle}
        aria-label={`Switch camera OSD (currently ${brand})`}
        className="absolute bottom-8 md:bottom-10 right-10 md:right-12 z-30 font-mono text-[9px] tracking-[0.25em] px-2 py-1 border border-foreground/20 bg-background/40 backdrop-blur-sm text-foreground/60 hover:text-foreground hover:border-accent transition-colors cursor-pointer"
      >
        <span className="opacity-50">CAM/</span>
        <span className={brand === "RED" ? "text-accent" : "text-[hsl(var(--osd-yellow))]"}>
          {brand === "ARRI" ? "ALEXA Mini LF" : "KOMODO-X"}
        </span>
      </button>

      {/* Main content — split/offset headline */}
      <div className="relative z-10 px-4 w-full max-w-[1100px] mx-auto">
        <div className="font-mono text-xs text-accent tracking-[0.3em] mb-6 opacity-0 animate-fade-up">
          {'>>'} VISUAL_ARCHITECT.V2
        </div>
        <h1 className="text-[clamp(2.75rem,12vw,10rem)] font-extrabold tracking-tighter text-foreground animate-fade-up glitch cursor-default leading-[0.82]">
          <span className="sr-only">Michał Wencek — operator kamery i drona, fotograf, produkcja wideo</span>
          <span aria-hidden="true">MICHAŁ</span>
        </h1>
        <div className="relative">
          {/* Mała estetyczna ramka ze zdjęciem — polaroid */}
          <div
            className="absolute right-[4vw] md:right-[2vw] z-20"
            style={{
              top: isMobile ? "-130px" : "-224px",
              opacity: photoLoaded ? undefined : 0,
              animation: photoLoaded
                ? "hero-shot-glide 1.1s cubic-bezier(0.22, 1, 0.36, 1) both"
                : undefined,
              ["--hero-shot-rotate" as string]: "-25deg",
            }}
          >
            <div
              className="bg-foreground p-1.5 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.8)] -rotate-[25deg] hover:-rotate-[18deg] transition-transform duration-500"
            >
              <img
                src={heroFramePhoto}
                alt="Polaroid z planu zdjęciowego — Michał Wencek, operator kamery"
                className="block w-[42px] sm:w-[56px] md:w-[110px] lg:w-[140px] h-auto object-cover"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width={140}
                height={175}
                onLoad={() => setPhotoLoaded(true)}
              />
            </div>
          </div>
          <p
            aria-hidden="true"
            className="text-[clamp(2.75rem,12vw,10rem)] font-extrabold tracking-tighter text-foreground animate-fade-up glitch cursor-default leading-[0.82] pl-[28vw] -mt-2 m-0"
            style={{ animationDelay: "0.15s" }}>
            WENCEK
          </p>
        </div>
        <div
          className="mt-6 flex items-center gap-3 animate-fade-up"
          style={{ animationDelay: "0.3s" }}>
          <div className="h-[2px] flex-1 bg-accent" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground whitespace-nowrap">
            [ {t.hero.subtitle1} ]
          </span>
        </div>
        <p
          className="mt-3 text-right font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground animate-fade-up"
          style={{ animationDelay: "0.45s" }}>
          [ {t.hero.subtitle2} ]
        </p>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-accent transition-colors cursor-pointer"
        aria-label="Scroll to about section">
        
        <ChevronDown className="w-8 h-8 animate-bounce-subtle" />
      </button>
    </section>);

};

export default HeroSection;